import { useEffect } from 'react';
import { useNavigate } from 'react-router';

/**
 * Leaves the auth screen once a session exists.
 *
 * Shared by login and signup because both are arrivals and the rule has to be
 * the same at each: a planner holding a session has no business sitting on a
 * sign-in form, and signup with `REQUIRE_EMAIL_VERIFICATION=false` is signed
 * in the moment it returns.
 *
 * Where they go next is deliberately not decided here. This hook used to ask
 * `setup/status` and route a new account to /welcome itself, and that put the
 * first-run rule on two screens that a Google sign-up renders neither of —
 * better-auth answers the OAuth callback with a 302 to the `callbackURL`, so a
 * brand new account arrived at `/` with no React auth screen in the path and
 * never saw setup. `FirstRunGate` now owns that decision inside the protected
 * shell, where every session passes regardless of how it was obtained, so this
 * hook has one job again.
 */
export function useLandAfterAuth(session: unknown, onLand?: () => void) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!session) return;
    onLand?.();
    navigate('/', { replace: true });
    // `onLand` is a stable cleanup callback from the caller; including it would
    // re-run the navigation on every render of a page that rebuilds it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, navigate]);
}
